import type {ComposerAttachment} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import type {ComposerAttachmentIcon} from '@github-ui/agent-sessions-interface/components/chat-composer-attachments'
import {INJECTED_ATTACHMENT_PREFIX} from '@github-ui/copilot-shared-ui/contexts/InjectedReferencesContext'

import {SELECTED_SPACE_ATTACHMENT_ID} from '../spaces/space-attachment'
import {isImplicitChipId} from '../task/use-implicit-draft-attachments'
import type {TelemetryAttachment} from './composer-telemetry'
import type {ComposerAttachmentOrigin, ComposerAttachmentType} from './constants'

const TYPE_BY_ICON: Record<ComposerAttachmentIcon, ComposerAttachmentType> = {
  file: 'file',
  folder: 'folder',
  repo: 'repository',
  commit: 'commit',
  discussion: 'discussion',
  diff: 'diff',
  release: 'release',
  snippet: 'snippet',
  link: 'link',
  workflow: 'workflow',
  issue: 'issue',
  'issue-open': 'issue',
  'issue-closed': 'issue',
  'issue-not-planned': 'issue',
  pull: 'pull_request',
  'pull-open': 'pull_request',
  'pull-closed': 'pull_request',
  'pull-merged': 'pull_request',
  'pull-draft': 'pull_request',
  space: 'space',
}

export function attachmentTypeForIcon(icon: ComposerAttachmentIcon | undefined): ComposerAttachmentType {
  return (icon && TYPE_BY_ICON[icon]) || 'unknown'
}

export function attachmentOriginForId(id: string): ComposerAttachmentOrigin {
  if (id === SELECTED_SPACE_ATTACHMENT_ID) return 'user'
  if (isImplicitChipId(id)) return 'implicit'
  if (id.startsWith(INJECTED_ATTACHMENT_PREFIX)) return 'injected'
  return 'user'
}

/** Projects composer chips to the label-free shape telemetry needs. Labels can contain PII and are dropped. */
export function toTelemetryAttachments(
  attachments: ReadonlyArray<Pick<ComposerAttachment, 'id' | 'icon'>>,
  imageIds: readonly string[] = [],
): TelemetryAttachment[] {
  return [
    ...attachments.map(attachment => ({
      id: attachment.id,
      type:
        attachment.id === SELECTED_SPACE_ATTACHMENT_ID ? ('space' as const) : attachmentTypeForIcon(attachment.icon),
      origin: attachmentOriginForId(attachment.id),
    })),
    ...imageIds.map(id => ({id: `image:${id}`, type: 'image' as const, origin: 'user' as const})),
  ]
}
